import { client } from 'src/enviroment/api/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mutation, Query } from '../index'
import {
  ChangePasswordBody,
  CreateUserBody,
  CurrentUser,
  JoinTenantBody,
  UpdateAvatarResponse,
  UpdateCurrentUserBody,
  UpdateCurrentUserResponse,
} from 'src/types/user'

export const userRouterKeys = {
  currentUser: 'user.currentUser',
} as const

export const userRouter = {
  create: {
    useMutation: (opts?: Mutation<CreateUserBody, CurrentUser>) => {
      const queryClient = useQueryClient()

      return useMutation({
        ...opts,
        mutationFn: async (body: CreateUserBody) => {
          return (await client.post<CurrentUser>('/users', body)).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            queryKey: [userRouterKeys.currentUser],
          })
          opts?.onSuccess?.(...args)
        },
      })
    },
  },
  changePassword: {
    useMutation: (opts?: Mutation<ChangePasswordBody, void>) => {
      return useMutation({
        mutationFn: async (body: ChangePasswordBody) => {
          await client.post('/password/change', body)
        },
        ...opts,
      })
    },
  },
  currentUser: {
    useQuery: (opts?: Query<CurrentUser>) => {
      return useQuery<CurrentUser>({
        queryKey: [userRouterKeys.currentUser],
        queryFn: async () => {
          return (await client.get<CurrentUser>('/users/myself')).data
        },
        ...opts,
      })
    },
    update: {
      useMutation: (opts?: Mutation<UpdateCurrentUserBody, UpdateCurrentUserResponse>) => {
        const queryClient = useQueryClient()

        return useMutation({
          ...opts,
          mutationFn: async (body: UpdateCurrentUserBody) => {
            return (await client.patch<UpdateCurrentUserResponse>('/users/myself', body)).data
          },
          onSuccess: (...args) => {
            queryClient.invalidateQueries({
              queryKey: [userRouterKeys.currentUser],
            })
            opts?.onSuccess?.(...args)
          },
        })
      },
    },
    delete: {
      useMutation: (opts?: Mutation<void, void>) => {
        const queryClient = useQueryClient()

        return useMutation({
          ...opts,
          mutationFn: async () => {
            await client.delete('/users/myself')
          },
          onSuccess: (...args) => {
            queryClient.invalidateQueries({
              queryKey: [userRouterKeys.currentUser],
            })
            opts?.onSuccess?.(...args)
          },
        })
      },
    },
    //Deprecated
    // onboardingFinish: {
    //   useMutation: (opts?: Mutation<void, void>) => {
    //     const queryClient = useQueryClient()

    //     return useMutation({
    //       mutationFn: async () => {
    //         await client.post('/users/myself/onboarding-finish')
    //       },
    //       onSuccess: (...args) => {
    //         queryClient.invalidateQueries({
    //           queryKey: [userRouterKeys.currentUser],
    //         })
    //         opts?.onSuccess?.(...args)
    //       },
    //       ...opts,
    //     })
    //   },
    // },
    joinTenant: {
      useMutation: (opts?: Mutation<JoinTenantBody, void>) => {
        const queryClient = useQueryClient()

        return useMutation({
          ...opts,
          mutationFn: async (body: JoinTenantBody) => {
            await client.patch('/users/myself:join-tenant', body)
          },
          onSuccess: (...args) => {
            queryClient.invalidateQueries({
              queryKey: [userRouterKeys.currentUser],
            })
            opts?.onSuccess?.(...args)
          },
        })
      },
    },
    updateAvatar: {
      useMutation: (opts?: Mutation<File, UpdateAvatarResponse>) => {
        const queryClient = useQueryClient()

        return useMutation({
          ...opts,
          mutationFn: async (file: File) => {
            const formData = new FormData()
            formData.append('avatar', file)

            return (
              await client.patch<UpdateAvatarResponse>('/users/myself/avatar', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              })
            ).data
          },
          onSuccess: (...args) => {
            queryClient.invalidateQueries({
              queryKey: [userRouterKeys.currentUser],
            })
            opts?.onSuccess?.(...args)
          },
        })
      },
    },
  },
}
